<template>
  <div class="content">
    <section class="listing-property g-page">
      <div class="mobile-search bg-white g-search-widget">
        <portal-target name="property-mobile-search"></portal-target>
        <div class="container-fluid plr-80 my-4 border-bottom border-top py-2">
          <div class="font-weight-bold">
            <span
              @click="$refs.filters.showFilters()"
              class="position-relative"
            >
              <b-icon-sliders></b-icon-sliders>
              {{ $t("listingFilter.filters") }}
              <span
                v-if="filtersApplied()"
                class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger text-white"
              >
                {{ filtersApplied() }}
              </span>
            </span>
          </div>
        </div>

        <div class="container-fluid plr-80 pb-4">
          <div>
            <p v-if="fetching && !hasTravelDates">
              {{ $t("listing.findingDeals") }}
            </p>
            <p v-if="hasTravelDates && fetching">
              {{ $t("listing.findingPropertiesForDates") }}
            </p>
          </div>
          <div v-if="!fetching && totalElements && currentConfigKey('showListingCount') && hasTravelDates">
            {{ $t("listing.showingAvailableProperties") }}
          </div>
          <div class="mt-2" v-if="totalElements && currentConfigKey('showListingCount') && !hasTravelDates">
            {{ totalElements | numberFormat }}
            {{ $t("listing.dealsAvailable") }}
          </div>
        </div>
      </div>
      <div class="container-fluid plr-80">
        <div class="row">
          <div class="col-md-4 col-lg-3 listing-left-sec">
            <PropertySearch @findPlaces="findPlaces" />
            <Filters
              ref="filters"
              :fetching="fetching"
              :results="totalElements || 0"
              @updated="applyFilters"
            />
          </div>
          <div class="col-md-8 col-lg-9 listing-right-sec px-md-3 px-0">
            <ListingTopbar
              :location="savedLocation"
              :destination="destination"
              :fetching="fetching"
              :totalElements="totalElements"
              :currentSort="currentSort"
              @sort-change="applySort"
              :hasTravelDates="hasTravelDates"
            />

            <div class="properties-list">
              <PropertyCard
                v-for="property of propertiesList"
                :key="property.id"
                :date="savedDate"
                :property="property"
                @openProperty="openProperty"
                :destination="destination"
                :location="savedLocation"
                :propertyUrl="propertyUrl(property.id)"
              />
              <div
                v-if="showOnRequestSection"
                class="on-request-listing-section"
              >
                <h2 class="listing-segment-title">
                  {{ $t("listing.hotelsBookableOnRequest") }}
                </h2>
                <PropertyCard
                  v-for="property of onRequestPropertiesList"
                  :key="`on-request-${property.id}`"
                  :date="savedDate"
                  :property="property"
                  :hide-price="true"
                  @openProperty="openProperty"
                  :destination="destination"
                  :location="savedLocation"
                  :propertyUrl="propertyUrl(property.id)"
                />
              </div>
              <InfiniteLoading
                ref="infiniteLoading"
                @infinite="loadMore"
                :identifier="infiniteId"
                v-if="useAutoLoad"
              >
                <div slot="spinner"></div>
                <div slot="no-results" class="my-5">
                  <div
                    v-if="!isLoading && !propertiesList.length && !onRequestPropertiesList.length"
                    class="no-properties-found text-center"
                  >
                    <img
                      src="@/assets/icons/no-properties.svg"
                      alt="No properties found"
                      class="no-properties-icon mb-3"
                    />
                    <h4 class="mb-2">{{ $t("listing.noPropertiesFound") }}</h4>
                    <p class="text-muted">{{ $t("listing.tryAdjustingFilters") }}</p>
                  </div>
                </div>
                <div slot="no-more" class="my-5"></div>
              </InfiniteLoading>
              <ListingSkeleton v-if="isLoading" class="mt-3" :count="hasTravelDates ? 2 : 10"/>
              <div v-if="showLoadMore" class="text-center my-4">
                <button class="btn g-outline-button" @click="manualLoadMore">
                  Load More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <Footer></Footer>
  </div>
</template>

<script>
import moment from "moment";
import { reloadRoute } from "../helpers";
import { defaultListId } from "../shared";
import { mapState, mapActions, mapGetters } from "vuex";
import Filters from "./Widgets/Filters.vue";
import { currentConfigKey } from "../helpers/theme";
import { buildPropertyUrl } from "../helpers/url";
import InfiniteLoading from "vue-infinite-loading";
import PropertyCard from "./Property/PropertyCard.vue";
import ListingTopbar from "./Property/ListingTopbar.vue";
import PropertySearch from "./Widgets/PropertySearch.vue";
import ListingSkeleton from "./Skeletons/ListingSkeleton.vue";

// Supplier id of Nuitee type-B properties (same rule as the backend: supplier 121 = Nuitee, otherwise eHotel)
const NUITEE_SUPPLIER_ID = 121;
const SPLIT_LISTING_MAX_PAGES = 20;

export default {
  name: "PropertyListing",
  components: {
    Filters,
    PropertyCard,
    ListingTopbar,
    PropertySearch,
    InfiniteLoading,
    ListingSkeleton,
  },
  data() {
    return {
      perPage: 15,
      listId: defaultListId,
      useAutoLoad: true,
      showLoadMore: false,
      isLoading: false,
      noMoreData: false,
      destination: "",
      currentPage: 1,
      searchRadius: 20,
      totalElements: 0,
      infiniteId: +new Date(),
      propertiesList: [],
      onRequestPropertiesList: [],
      listingComplete: false,
      googlePlacesMap: {},
      location: {
        destination: "",
        latitude: 0,
        longitude: 0,
      },
      savedLocation: {
        destination: "",
        latitude: 0,
        longitude: 0,
        filter: {},
      },
      date: {
        checkIn: null,
        checkOut: null,
      },
      savedDate: {
        checkIn: null,
        checkOut: null,
      },
      currentSort: "default",
      availabilityFetchMaxAttempts: 3,
    };
  },
  created() {
    const { latitude, longitude, destination, checkIn, checkOut, sort } =
      this.$route.query;
    if (latitude && longitude) {
      const coords = { latitude, longitude };
      this.location = { ...this.location, ...coords };
      this.savedLocation = { ...this.savedLocation, ...coords };

      this.destination = destination;
    }

    if (checkIn && checkOut) {
      const formattedDate = {
        checkIn: moment(checkIn, "YYYY-MM-DD").format("DD MMM YYYY"),
        checkOut: moment(checkOut, "YYYY-MM-DD").format("DD MMM YYYY"),
      };

      Object.assign(this.date, formattedDate);
      Object.assign(this.savedDate, formattedDate);
    }

    if (sort) {
      this.currentSort = sort;
    }

    this.listId = this.bookingEngineId;
  },
  async mounted() {
    await this.$nextTick();
    const el = this.$refs.filters;
    if (el) {
      const { checkIn, checkOut } = this.parsedDate;
      await el.loadFacilitiesCount({
        checkIn,
        checkOut,
        latitude: null,
        longitude: null,
        list_id: this.listId,
        radius: this.searchRadius,
      });
    }
  },
  computed: {
    ...mapState("property", ["properties", "fetching"]),
    ...mapState("config", ["currentListId"]),
    ...mapGetters("config", ["bookingEngineId"]),
    parsedDate() {
      let { checkIn, checkOut } = this.date;
      if (checkIn && checkOut) {
        checkIn = moment(checkIn, "DD MMM YYYY").format("YYYY-MM-DD");
        checkOut = moment(checkOut, "DD MMM YYYY").format("YYYY-MM-DD");
      } else {
        checkIn = null;
        checkOut = null;
      }
      return { checkIn, checkOut };
    },
    hasTravelDates() {
      const { checkIn, checkOut } = this.parsedDate;
      return !!(checkIn && checkOut);
    },
    separateUnconfirmedAvailability() {
      return !!currentConfigKey("separateUnconfirmedAvailability", false);
    },
    showOnRequestSection() {
      return (
        this.separateUnconfirmedAvailability &&
        this.hasTravelDates &&
        this.listingComplete &&
        this.onRequestPropertiesList.length > 0
      );
    },
  },
  methods: {
    ...mapActions("property", ["searchProperties", "fetchMinRates", "fetchFromRates"]),
    currentConfigKey,
    filtersApplied() {
      const el = this.$refs.filters;
      this.savedLocation.filter = el?.getFilters();
      return el ? el.filtersApplied() : 0;
    },
    getPropertyPayload() {
      const { latitude, longitude } = this.savedLocation;

      let payload = {
        latitude: latitude || null,
        longitude: longitude || null,
        radius: this.searchRadius,
        page: this.currentPage,
        perPage: this.perPage,
        checkIn: this.date.checkIn,
        checkOut: this.date.checkOut,
        list_id: this.listId,
        ...this.$refs.filters.getFilters(),
      };

      if (this.currentSort && this.currentSort !== "default") {
        payload.sort = this.currentSort;
      }

      this.location.filter = this.$refs.filters.getFilters();
      const { checkIn, checkOut } = this.date;

      if (checkIn && checkOut) {
        payload = {
          ...payload,
          checkIn: moment(checkIn, "DD MMM YYYY").format("YYYY-MM-DD"),
          checkOut: moment(checkOut, "DD MMM YYYY").format("YYYY-MM-DD"),
        };
      }

      return payload;
    },
    async manualLoadMore() {
      if (this.isLoading || this.noMoreData) {
        return;
      }

      this.isLoading = true;

      try {
        if (!this.hasTravelDates) {
          this.useAutoLoad = false;
        }
        const { appendedCount } = await this.fetchPropertiesPage();

        if (this.noMoreData) {
          this.showLoadMore = false;
        } else if (appendedCount === 0) {
          this.showLoadMore = false;
        }
      } catch (error) {
        console.error("Error fetching items:", error);
      }

      this.isLoading = false;
    },
    async loadMore($state) {
      if (this.isLoading || this.noMoreData) {
        this.finishInfiniteLoad($state);
        return;
      }

      this.isLoading = true;

      try {
        const { appendedCount, hasMorePages } = await this.fetchPropertiesPage();

        if (this.noMoreData) {
          this.finishInfiniteLoad($state);
        } else if (appendedCount === 0 && !hasMorePages) {
          this.finishInfiniteLoad($state);
        } else {
          $state.loaded();
          if (
            !this.hasTravelDates &&
            appendedCount > 0 &&
            this.currentPage > 3 &&
            this.totalElements > this.propertiesList.length
          ) {
            this.useAutoLoad = false;
            this.showLoadMore = true;
            $state.complete();
          }
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        $state.error();
      }

      this.isLoading = false;
    },
    finishInfiniteLoad($state) {
      this.listingComplete = true;
      $state.complete();
    },
    fetchPropertiesPage() {
      return this.hasTravelDates
        ? this.fetchAndAppendAvailableProperties()
        : this.fetchAndAppendAllProperties();
    },
    async fetchAndAppendAllProperties() {
      const payload = this.getPropertyPayload();
      const data = await this.searchProperties(payload);

      if (!data?.content?.length) {
        this.noMoreData = true;
        return { appendedCount: 0, hasMorePages: false };
      }

      this.totalElements = data.totalElements;

      const batch = JSON.parse(JSON.stringify(data.content));
      batch.forEach((property) => this.normalizeListingProperty(property));
      await this.resolveMinRatesForBatch(batch);
      batch.forEach((property) => this.appendListingProperty(property));

      this.currentPage++;

      const len = data.content.length;
      return {
        appendedCount: len,
        hasMorePages: len === this.perPage,
      };
    },
    async fetchAndAppendAvailableProperties() {
      const collectedProperties = [];
      const collectAllPages = this.separateUnconfirmedAvailability;
      const maxAttempts = collectAllPages
        ? SPLIT_LISTING_MAX_PAGES
        : this.availabilityFetchMaxAttempts;
      let lastPageContentLength = 0;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const payload = this.getPropertyPayload();
        const data = await this.searchProperties(payload);

        if (!data?.content?.length) {
          this.noMoreData = true;
          lastPageContentLength = 0;
          break;
        }

        lastPageContentLength = data.content.length;
        this.totalElements = data.totalElements;

        const batch = JSON.parse(JSON.stringify(data.content));
        batch.forEach((property) => this.normalizeListingProperty(property));
        await this.resolveMinRatesForBatch(batch);

        const available = batch.filter((property) =>
          this.shouldIncludeInDateSearch(property)
        );
        available.forEach((p) => collectedProperties.push(p));

        this.currentPage++;

        if (collectAllPages) {
          const fetchedCount = (this.currentPage - 1) * this.perPage;
          const fetchedAll =
            lastPageContentLength < this.perPage ||
            (typeof this.totalElements === "number" &&
              fetchedCount >= this.totalElements);
          if (fetchedAll) {
            this.noMoreData = true;
            break;
          }
          continue;
        }

        if (available.length > 0) {
          break;
        }
      }

      if (collectAllPages) {
        this.noMoreData = true;
      }

      collectedProperties.forEach((property) => {
        const list =
          this.separateUnconfirmedAvailability &&
          this.isOnRequestListingProperty(property)
            ? this.onRequestPropertiesList
            : this.propertiesList;
        this.appendListingProperty(property, list);
      });

      const appendedCount = collectedProperties.length;
      const hasMorePages =
        !this.noMoreData && lastPageContentLength === this.perPage;

      return { appendedCount, hasMorePages };
    },
    normalizeListingProperty(property) {
      property.ratingCount =
        property.googleReviews?.googleNumberOfReviews || 0;
      property.ratingValue = property.googleReviews?.googleRating || 0;
      property.availability = property.availabilities?.length
        ? this.computeAvailabilityPrice(property.availabilities, property.checkOutAvailability)
        : null;

      if (property.media) {
        property.media
          .map((m) => {
            m.order = m.sortOrderMain || Number.MAX_VALUE;
            return m;
          })
          .sort((a, b) => a.order - b.order)
          .slice(0, 4);
      }
    },
    shouldIncludeInDateSearch(property) {
      const { availability, distributorType, priceOnRequest } = property;
      if (this.separateUnconfirmedAvailability) {
        if (["C", "D"].includes(distributorType) || distributorType === "B") {
          return true;
        }
        return availability && availability.price > 0;
      }
      if (["C", "D"].includes(distributorType)) {
        return true;
      }
      return (availability && availability.price > 0) || priceOnRequest === true;
    },
    isOnRequestListingProperty(property) {
      const { availability, distributorType } = property;
      if (["C", "D"].includes(distributorType)) {
        return true;
      }
      if (distributorType === "B") {
        return !(availability && availability.price > 0);
      }
      return false;
    },
    appendListingProperty(property, targetList = this.propertiesList) {
      const googlePlaceID = property?.googleReviews?.googleBusinessPlacesId;
      const distributorType = property?.distributorType;
      if (googlePlaceID && googlePlaceID in this.googlePlacesMap) {
        const { type, list, item } = this.googlePlacesMap[googlePlaceID];
        const priorities = currentConfigKey("distributorOrder", []);
        const newPriority = priorities[distributorType] ?? Infinity;
        const currentPriority = priorities[type] ?? Infinity;
        if (newPriority < currentPriority) {
          const sourceList = list || this.propertiesList;
          const index = sourceList.findIndex(
            (p) => p === item || (item && p.id === item.id)
          );
          if (index !== -1) {
            sourceList.splice(index, 1);
          }
        } else {
          return;
        }
      }
      targetList.push(property);
      if (googlePlaceID) {
        this.googlePlacesMap[googlePlaceID] = {
          type: distributorType,
          list: targetList,
          item: property,
        };
      }
    },
    buildAvailabilityPrice(availabilities) {
      const { checkIn, checkOut } = this.parsedDate;
      if (!availabilities.length) {
        return {
          price: 0,
          currency: null,
        };
      }

      const availabilityMap = availabilities.reduce((map, item) => {
        map[item.date] = item;
        return map;
      }, {});

      let total = 0;

      const startDate = new Date(checkIn);
      const endDate = new Date(checkOut);
      const nights = Math.round((endDate - startDate) / 86400000);
      let isArrivalNight = true;

      while (startDate < endDate) {
        const date = startDate.toISOString().split("T")[0];
        const availability = availabilityMap[date];

        // A night without price is not bookable, so the stay has no listing price (#239)
        if (availability?.minPrice == null) {
          return 0;
        }

        // First night: cheapest rate open for arrival that allows this length of stay (#240);
        // snapshots without arrivalPrices keep using minPrice
        if (isArrivalNight && Array.isArray(availability.arrivalPrices)) {
          const arrivalPrice = this.findArrivalPrice(availability.arrivalPrices, nights);
          if (arrivalPrice == null) {
            return 0;
          }
          total += arrivalPrice;
        } else {
          total += availability.minPrice;
        }

        isArrivalNight = false;
        startDate.setDate(startDate.getDate() + 1);
      }

      return total;
    },
    findArrivalPrice(arrivalPrices, nights) {
      const prices = arrivalPrices
        .filter(
          ({ minLos, maxLos }) =>
            (minLos == null || minLos <= nights) && (maxLos == null || maxLos >= nights),
        )
        .map(({ price }) => price);
      return prices.length ? Math.min(...prices) : null;
    },
    computeAvailabilityPrice(availabilities, checkOutAvailability) {
      return {
        // Check-out date closed for departure: the stay is not bookable (#240)
        price:
          checkOutAvailability?.departureAllowed === false
            ? 0
            : this.buildAvailabilityPrice(availabilities),
        currency: availabilities[0].currencyCode,
      };
    },
    async resolveMinRatesForBatch(batch) {
      if (!batch?.length) return;
      const { checkIn, checkOut } = this.parsedDate;
      if (!checkIn || !checkOut) return;
      const isNuitee = (property) => property.supplierId === NUITEE_SUPPLIER_ID;
      const toRequest = ({ id, distributorType, supplierId }) => ({ id, supplierId, distributorType });
      const propertyById = new Map(batch.map((property) => [String(property.id), property]));
      const applyRates = (result) => {
        if (!result || typeof result !== "object") return;
        Object.entries(result).forEach(([id, availability]) => {
          const property = propertyById.get(String(id));
          if (property) {
            property.availability = {
              price: availability.price ?? 0,
              currency: availability.currency || property.availability?.currency,
            };
          }
        });
      };

      // Type B from eHotel: stored eHotel from-rate for the stay, no live eHotel call (#238).
      // Without a stored from-rate the snapshot price is kept.
      const eHotel = batch.filter((p) => p.distributorType === "B" && !isNuitee(p));
      // Type B from Nuitee: unchanged, live min-rates if the snapshot price is missing or older than 2 hours
      const staleCutoff = moment().subtract(2, "hour");
      const isMissingOrStaleAvailability = (availability, lastUpdated = null) =>
        !availability ||
        !availability.price ||
        availability.price <= 0 ||
        !lastUpdated ||
        moment(lastUpdated).isBefore(staleCutoff);
      const nuitee = batch.filter(
        (p) =>
          p.distributorType === "B" &&
          isNuitee(p) &&
          isMissingOrStaleAvailability(p.availability, p.availabilitiesLastUpdate)
      );

      const [fromRates, minRates] = await Promise.all([
        eHotel.length ? this.fetchFromRates({ properties: eHotel.map(toRequest), checkIn, checkOut }) : null,
        nuitee.length ? this.fetchMinRates({ properties: nuitee.map(toRequest), checkIn, checkOut }) : null,
      ]);
      const storedFromRates = fromRates?.data ?? fromRates ?? {};
      applyRates(storedFromRates);
      applyRates(minRates?.data ?? minRates);

      // eHotel type B with neither a stored from-rate nor a snapshot price: show without price instead of hiding,
      // so guests can still open the property and their search stores a from-rate (#238)
      eHotel
        .filter((p) => !(String(p.id) in storedFromRates) && !(p.availability?.price > 0))
        .forEach((p) => {
          p.availability = null;
          p.priceOnRequest = true;
        });
    },
    async findPlaces({ location, date }) {
      this.date = date;
      this.location = location;
      this.totalElements = 0;
      if (this.location.propertyId) {
        return this.openProperty(this.location.propertyId);
      }
      const { latitude, longitude, destination } = this.location;
      this.savedLocation = {
        latitude,
        longitude,
        destination,
        filter: this.$refs.filters,
      };
      const { checkIn, checkOut } = this.date;
      this.savedDate = { checkIn, checkOut };
      this.resetPropertyList();
    },
    resetPropertyList() {
      this.currentPage = 1;
      this.propertiesList = [];
      this.onRequestPropertiesList = [];
      this.listingComplete = false;
      this.googlePlacesMap = {};
      this.noMoreData = false;
      this.useAutoLoad = true;
      this.showLoadMore = false;
      this.infiniteId += 1;
      this.currentSort = "default";
      const queryParams = { ...this.$route.query };
      delete queryParams.sort;
      reloadRoute(queryParams);
    },
    applyFilters() {
      this.resetPropertyList();
    },
    propertyUrl(id) {
      return buildPropertyUrl(id, this.date);
    },
    openProperty(id) {
      window.open(this.propertyUrl(id), "_blank");
    },
    applySort(sortValue) {
      if (this.currentSort === sortValue) {
        return;
      }

      this.currentSort = sortValue;
      const queryParams = { ...this.$route.query };
      if (sortValue === "default") {
        delete queryParams.sort;
      } else {
        queryParams.sort = sortValue;
      }
      reloadRoute(queryParams);

      this.currentPage = 1;
      this.propertiesList = [];
      this.onRequestPropertiesList = [];
      this.listingComplete = false;
      this.googlePlacesMap = {};
      this.noMoreData = false;
      this.infiniteId += 1;
      if (this.hasTravelDates) {
        this.useAutoLoad = true;
        this.showLoadMore = false;
      }
    },
  },
  watch: {
    hasTravelDates(val) {
      if (val) {
        this.useAutoLoad = true;
        this.showLoadMore = false;
      }
    },
    savedLocation: {
      async handler(
        { latitude, longitude } = {},
        { latitude: oldLat, longitude: oldLng } = {}
      ) {
        if (latitude == oldLat && longitude == oldLng) return;
        const el = this.$refs.filters;
        if (el) {
          const { checkIn, checkOut } = this.parsedDate;
          await el.loadFacilitiesCount({
            checkIn,
            checkOut,
            latitude,
            longitude,
            radius: this.searchRadius,
            list_id: this.listId,
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style>
@import "../assets/css/calendar.css";
</style>

<style scoped>
.properties-list {
  min-height: 40vh;
}
.listing-segment-title {
  margin: 52px 0 8px;
}
.on-request-listing-section {
  clear: both;
}
.typesense-search-widget {
  width: 400px;
}
@media screen and (max-width: 767px) {
  .typesense-search-widget {
    top: 140px !important;
    width: 90% !important;
  }
}
</style>
